import React, { useEffect, useState } from 'react';
import TextInput from '@/modules/TextInput';
import { IBasket, IEcProduct } from '@/types/simo/basket';
import { addContactInfo } from '@/api/simo';
import { getSessionStorage } from '@/utils/session';
import './_contact.scss';
import Button from '@/modules/Button';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { view } from '@nuuday/data-layer';
import { legalTextRenderOptions } from '@/contentful/helper';
import SoftErrorModal from '@/components/error-boundary/SoftErrorModal';

interface IProps {
  setActiveSection: () => void;
  basket: IBasket;
  setBasket: React.Dispatch<React.SetStateAction<any>>;
  checkoutCms: any;
  ecProducts: IEcProduct[];
}

const Contact = (props: IProps) => {
  const { setActiveSection, basket, setBasket, ecProducts, checkoutCms } = props;
  const [loading, setLoading] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [email, setEmail] = useState(basket.orderContactInfo ? basket.orderContactInfo.email : '');
  const [phoneNumber, setPhoneNumber] = useState(
    basket.orderContactInfo ? basket.orderContactInfo.phoneNumber : ''
  );

  const [softError, toggleSoftError] = useState(false);

  useEffect(() => {
    view({
      pageName: 'contact',
      siteSection: ['i hjemmet', 'mobil', 'simonly'],
      customEvent: ['ecCheckout'],
      pageType: 'simo checkout 4/8',
      ecProducts,
      mvGrossAdds: ecProducts?.length || 0
    });
  }, []);
  const { firstName, lastName, /* mustBeCprValidated */ address } = basket.customerInfo;
  const onContinue = async () => {
    try {
      let errors = {};
      const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      setLoading(true);

      if (!email || !emailPattern.test(email)) {
        errors = { ...errors, email: 'Indsæt venligst en email' };
      }
      if (!phoneNumber || phoneNumber.length < 8) {
        errors = { ...errors, phoneNumber: 'Indsæt venligst et mobilnummer' };
      }
      if (Object.keys(errors).length === 0) {
        const session = getSessionStorage('_session');
        const response = await addContactInfo({
          email,
          mobileNumber: phoneNumber,
          _session: session
        });
        setBasket(response);
        setLoading(false);
        setActiveSection();
      } else {
        setInputErrors(errors);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      toggleSoftError(true);
    }
  };
  const fullName = (name: string, surname: string) => (surname ? `${name} ${surname}` : name);
  const setEmailOnChange = (value: string) => {
    if (inputErrors['email']) {
      inputErrors['email'] = false;
    }

    setEmail(value);
  };

  const setPhoneNumberOnChange = (value: string) => {
    if (inputErrors['phoneNumber']) {
      inputErrors['phoneNumber'] = false;
    }
    setPhoneNumber(value);
  };

  const legalTextContent = checkoutCms?.checkoutCms?.contactLegalText
    ? checkoutCms.checkoutCms.contactLegalText.json
    : '';

  return (
    <>
      {softError ? (
        <SoftErrorModal isShown={softError} />
      ) : (
        <section className="contact">
          <div className="contact__name">
            {firstName && <div data-hj-suppress>{fullName(firstName, lastName)}</div>}
            {address && (
              <>
                <div data-hj-suppress>{address.streetAndNumber}</div>
                <div data-hj-suppress>{address.postalCodeAndCity}</div>
              </>
            )}
          </div>
          <div>
            <TextInput
              type="email"
              name="email"
              label={'EMAIL'}
              data-hj-suppress
              placeholder={'Indtast dit email'}
              value={email}
              onChange={(value: string) => {
                setEmailOnChange(value);
              }}
              className="contact__details col-12 col-md-8 row"
              error={inputErrors['email']}
            />
            <div className="contact__details__info">
              {documentToReactComponents(checkoutCms.checkoutCms.contactEmailText.json)}
            </div>
            <TextInput
              type="number"
              data-hj-suppress
              name="phoneNumber"
              maxLength="8"
              label={'TELEFONNUMMER'}
              placeholder={'Indtast venligst et 8-cifret telefonnummer'}
              telOnly={true}
              value={phoneNumber}
              onChange={(value: string) => {
                setPhoneNumberOnChange(value);
              }}
              className="contact__details col-12 col-md-8 row"
              error={inputErrors['phoneNumber']}
            />
            <div className="contact__details__info">
              {documentToReactComponents(checkoutCms.checkoutCms.contactTelephoneNumberText.json)}
            </div>
          </div>
          <div className="contact__button">
            <Button
              display="block-centered"
              value="Fortsæt"
              onClick={onContinue}
              loading={loading}
            />
          </div>
          <div className="contact__legal-text">
            {documentToReactComponents(legalTextContent, legalTextRenderOptions)}
          </div>
        </section>
      )}
    </>
  );
};

export default Contact;
